import { PropsWithChildren, FC } from 'react';

import { I18nTranslationProvider } from './i18n';
import { Locale, LocaleProvider } from './locale';
import { MediaQueryProvider } from './mediaQuery';
import { ToastProvider } from '../components/Toast/Toast';
import { useIsomorphicLayoutEffect } from '../hooks/useIsomorphicLayoutEffect';
import { themeClassNames } from '../theme.css';
import { getClassNames } from '../util/css';
import getI18nTranslations from '../util/i18nTranslations';

import '../styles/global.css';

export const THEME_DATA_IDENTIFIER = 'themeclasses';

interface Props {
  locale?: Locale;
  platformHint?: 'desktop' | 'mobile';
  theme?: string;
  /**
   * When true, prevents SparkyProvider from adding theme classes to document.body.
   * Use this when rendering in isolated contexts like Shadow DOM to avoid conflicts
   * with other SparkyProvider instances on the page.
   */
  disableBodyThemeClasses?: boolean;
}

export const SparkyProvider: FC<PropsWithChildren<Props>> = ({
  children,
  locale = 'nl-NL',
  platformHint,
  theme = 'default',
  disableBodyThemeClasses = false,
}) => {
  const themeClasses = getClassNames(themeClassNames[theme] ?? themeClassNames['default']);

  // While the theme class is added by the SparkyProvider, theming isn't applied to elements (that are added) outside
  //  of the Provider (for isntance dialogs, that are appended as immediate children of the body). For these cases, the
  //  SparkyProvider also adds the theme classes to the body.
  useIsomorphicLayoutEffect(() => {
    if (disableBodyThemeClasses) {
      return;
    }

    // Check whether theme classes were already added to the body, so we can remove them before adding the new ones
    const existingThemeClasses = document.body.dataset[THEME_DATA_IDENTIFIER]?.split(' ') ?? [];

    document.body.classList.remove(...existingThemeClasses);
    document.body.classList.add(...themeClasses.split(' '));

    document.body.dataset[THEME_DATA_IDENTIFIER] = themeClasses;
  }, [theme, themeClasses, disableBodyThemeClasses]);

  return (
    <div className={themeClasses}>
      <ToastProvider>
        <MediaQueryProvider platformHint={platformHint}>
          <LocaleProvider locale={locale}>
            <I18nTranslationProvider labels={getI18nTranslations(locale)}>{children}</I18nTranslationProvider>
          </LocaleProvider>
        </MediaQueryProvider>
      </ToastProvider>
    </div>
  );
};
